import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { toast } from "react-toastify";

import apiService from "../services/api";

const DEFAULT_LIMIT = 50;

export const LEAGUE_SEARCH_DEBOUNCE_MS = 500;
export const MIN_LEAGUE_SEARCH_LENGTH = 2;

const OVERRIDE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const OVERRIDE_IMAGE_MIME_BY_EXTENSION = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

const emptyForm = {
  displayName: "",
  imageUrl: "",
  altName: "",
  sport: "",
  country: "",
  formedYear: "",
  reason: "",
};

function compactPayload(form) {
  return {
    displayName: form.displayName.trim() || null,
    imageUrl: form.imageUrl.trim() || null,
    altName: form.altName.trim() || null,
    sport: form.sport.trim() || null,
    country: form.country.trim() || null,
    formedYear: form.formedYear.trim() || null,
    reason: form.reason.trim() || null,
    enabled: true,
  };
}

function formFromLeague(league) {
  const override = league?.override || {};
  const data = override.data || {};

  return {
    displayName: override.displayName || "",
    imageUrl: override.imageUrl || "",
    altName: data.altName || "",
    sport: data.sport || "",
    country: data.country || "",
    formedYear: data.formedYear || "",
    reason: override.reason || "",
  };
}

function visibleLogo(league) {
  return league?.effectiveLogo || league?.logo || "";
}

function categoryLabel(categories, categoryKey) {
  return (
    categories.find((category) => category.key === categoryKey)?.name ||
    categoryKey ||
    "Uncategorized"
  );
}

function providerRefLabel(ref) {
  return [ref.provider, ref.providerKey, ref.providerId]
    .filter(Boolean)
    .join(" · ");
}

function hasEnabledOverride(league) {
  const override = league?.override;

  return Boolean(override && override.enabled !== false);
}

function hasOverrideValues(form) {
  return Object.entries(form).some(
    ([field, value]) => field !== "reason" && value.trim()
  );
}

function formatErrorDetails(details) {
  if (!details) return "";
  if (Array.isArray(details)) return details.filter(Boolean).join(". ");
  if (typeof details === "string") return details;
  if (typeof details === "object") {
    return Object.values(details).flat().filter(Boolean).join(". ");
  }

  return "";
}

export function formatSportsErrorMessage(error, fallback) {
  const data = error?.response?.data || {};
  const detailMessage = formatErrorDetails(error?.details || data.details);
  const genericError = data.error === "Bad Request";

  return (
    detailMessage ||
    (!genericError && data.error) ||
    data.message ||
    data.error ||
    error?.message ||
    fallback
  );
}

export function validateSportsOverrideImageFile(file) {
  if (!file) return "No image file uploaded";
  if (file.size === 0) return "No image file uploaded";
  if (file.size > OVERRIDE_IMAGE_MAX_BYTES) {
    return "image file must be 5MB or smaller";
  }

  const extension = file.name?.split(".").pop()?.toLowerCase() || "";
  const expectedMime = OVERRIDE_IMAGE_MIME_BY_EXTENSION[extension];

  if (!expectedMime) {
    return "image file must be jpg, jpeg, png, or webp";
  }

  if (file.type && file.type !== expectedMime) {
    return `image MIME type must be ${expectedMime}`;
  }

  return null;
}

export function normalizeLeagueSearch(value) {
  const text = String(value || "").trim();

  return text.length >= MIN_LEAGUE_SEARCH_LENGTH ? text : "";
}

const ImagePreview = ({ src, alt, className = "w-12 h-12" }) => {
  if (!src) {
    return (
      <div
        className={`${className} rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500 ring-1 ring-gray-200 dark:ring-gray-600`}
      >
        <i className="fas fa-image" />
      </div>
    );
  }

  return (
    <img
      alt={alt}
      className={`${className} rounded-lg object-contain bg-gray-100 dark:bg-gray-900 ring-1 ring-gray-200 dark:ring-gray-700`}
      src={src}
    />
  );
};

const ManageSports = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [leagues, setLeagues] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  const [loadingSelection, setLoadingSelection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);
  const leagueRequestSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(normalizeLeagueSearch(search));
    }, LEAGUE_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await apiService.getDashboardSportsCategories();

      setCategories(response.data || []);
    } catch (error) {
      toast.error(
        formatSportsErrorMessage(error, "Failed to load sports categories")
      );
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const loadLeagues = useCallback(async () => {
    const requestId = leagueRequestSeq.current + 1;

    leagueRequestSeq.current = requestId;
    setLoadingLeagues(true);
    try {
      const response = await apiService.getDashboardSportsLeagues({
        category: selectedCategory || undefined,
        q: debouncedSearch || undefined,
        limit: DEFAULT_LIMIT,
      });
      const rows = response.data || [];

      if (requestId !== leagueRequestSeq.current) return;
      setLeagues(rows);
      setTotal(response.total ?? rows.length);
    } catch (error) {
      if (requestId !== leagueRequestSeq.current) return;
      toast.error(
        formatSportsErrorMessage(error, "Failed to load sports leagues")
      );
      setLeagues([]);
      setTotal(0);
    } finally {
      if (requestId === leagueRequestSeq.current) {
        setLoadingLeagues(false);
      }
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadLeagues();
  }, [loadLeagues]);

  const selectLeague = async (league) => {
    if (!league?.canonicalId) return;
    setDisableConfirmOpen(false);
    setSelectedLeague(league);
    setForm(formFromLeague(league));
    setLoadingSelection(true);

    try {
      const response = await apiService.getDashboardSportsLeagueOverride(
        league.canonicalId
      );
      const latest = response.data || league;

      setSelectedLeague(latest);
      setForm(formFromLeague(latest));
    } catch (error) {
      toast.error(
        formatSportsErrorMessage(error, "Failed to load league override")
      );
    } finally {
      setLoadingSelection(false);
    }
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const refreshSelectedLeague = async (canonicalId) => {
    const response =
      await apiService.getDashboardSportsLeagueOverride(canonicalId);
    const latest = response.data;

    setSelectedLeague(latest);
    setForm(formFromLeague(latest));
    await loadLeagues();

    return latest;
  };

  const handleSave = async () => {
    if (!selectedLeague?.canonicalId) return;

    if (!hasOverrideValues(form)) {
      toast.error("At least one override field is required");

      return;
    }

    setSaving(true);
    try {
      await apiService.saveDashboardSportsLeagueOverride(
        selectedLeague.canonicalId,
        compactPayload(form)
      );
      await refreshSelectedLeague(selectedLeague.canonicalId);
      toast.success("Sports override saved");
    } catch (error) {
      toast.error(
        formatSportsErrorMessage(error, "Failed to save sports override")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!selectedLeague?.canonicalId || !hasEnabledOverride(selectedLeague))
      return;

    setSaving(true);
    try {
      await apiService.disableDashboardSportsLeagueOverride(
        selectedLeague.canonicalId
      );
      await refreshSelectedLeague(selectedLeague.canonicalId);
      setDisableConfirmOpen(false);
      toast.success("Sports override disabled");
    } catch (error) {
      toast.error(
        formatSportsErrorMessage(error, "Failed to disable sports override")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";
    if (!file || !selectedLeague?.canonicalId) return;

    const validationError = validateSportsOverrideImageFile(file);

    if (validationError) {
      toast.error(validationError);

      return;
    }

    setUploading(true);
    try {
      const response =
        await apiService.uploadDashboardSportsLeagueOverrideImage(
          selectedLeague.canonicalId,
          file
        );
      const latest =
        response.data?.league ||
        (await refreshSelectedLeague(selectedLeague.canonicalId));

      setSelectedLeague(latest);
      setForm(formFromLeague(latest));
      await loadLeagues();
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error(formatSportsErrorMessage(error, "Failed to upload logo"));
    } finally {
      setUploading(false);
    }
  };

  const selectedCategoryName = categoryLabel(categories, selectedCategory);
  const overriddenCount = useMemo(
    () => leagues.filter(hasEnabledOverride).length,
    [leagues]
  );
  const originalLogo = selectedLeague?.logo || "";
  const formLogo = form.imageUrl.trim() || originalLogo;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary-600 dark:text-primary-300">
            <i className="fas fa-trophy" />
            Manage Sports
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Sports Overrides
          </h1>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-0 xl:min-w-[420px]">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
              Categories
            </p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {loadingCategories ? "..." : categories.length}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
              Leagues
            </p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {loadingLeagues ? "..." : total}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
              Overrides
            </p>
            <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {overriddenCount}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 sm:p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_auto] gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-1">
              Category
            </label>
            <select
              className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
              disabled={loadingCategories}
              value={selectedCategory}
              onChange={(event) => setSelectedCategory(event.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-1">
              League Search
            </label>
            <div className="relative">
              <input
                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 py-2 pl-9 pr-3 text-sm text-gray-900 dark:text-white"
                placeholder="Search by name or canonical ID"
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          <div className="flex items-end">
            <button
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
              disabled={loadingLeagues}
              type="button"
              onClick={loadLeagues}
            >
              <i
                className={`fas fa-sync-alt ${loadingLeagues ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(360px,440px)_1fr] gap-4">
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Leagues
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {selectedCategory ? selectedCategoryName : "All Categories"}
              </p>
            </div>
            {loadingLeagues && (
              <i className="fas fa-spinner fa-spin text-gray-400" />
            )}
          </div>

          <div className="max-h-[680px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {!loadingLeagues && leagues.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <i className="fas fa-search text-2xl mb-2" />
                <p>No leagues found</p>
              </div>
            ) : (
              leagues.map((league) => {
                const active =
                  selectedLeague?.canonicalId === league.canonicalId;
                const overridden = hasEnabledOverride(league);

                return (
                  <button
                    className={`w-full text-left p-4 transition-colors ${
                      active
                        ? "bg-primary-50 dark:bg-primary-900/20"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    }`}
                    key={league.canonicalId}
                    type="button"
                    onClick={() => selectLeague(league)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ImagePreview
                        alt={league.effectiveName || league.name}
                        src={visibleLogo(league)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {league.effectiveName || league.name}
                          </p>
                          {overridden && (
                            <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-300">
                              Override
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {league.canonicalId}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {categoryLabel(categories, league.category)}
                        </p>
                      </div>
                      <i className="fas fa-chevron-right text-xs text-gray-300 dark:text-gray-600" />
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          {!selectedLeague ? (
            <div className="min-h-[520px] flex flex-col items-center justify-center text-center p-8 text-gray-500 dark:text-gray-400">
              <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                <i className="fas fa-shield-alt text-xl" />
              </div>
              <p className="font-medium text-gray-700 dark:text-gray-200">
                Select a league
              </p>
            </div>
          ) : (
            <>
              <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <ImagePreview
                      alt={selectedLeague.effectiveName || selectedLeague.name}
                      className="w-16 h-16"
                      src={formLogo}
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                          {selectedLeague.effectiveName || selectedLeague.name}
                        </h2>
                        {hasEnabledOverride(selectedLeague) && (
                          <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-300">
                            Enabled
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {selectedLeague.canonicalId}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {categoryLabel(categories, selectedLeague.category)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <input
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      ref={fileInputRef}
                      type="file"
                      onChange={handleImageUpload}
                    />
                    <button
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 dark:bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 dark:hover:bg-gray-600 disabled:opacity-60"
                      disabled={uploading || loadingSelection}
                      type="button"
                      onClick={handleUploadClick}
                    >
                      <i
                        className={`fas ${uploading ? "fa-spinner fa-spin" : "fa-upload"}`}
                      />
                      Upload Logo
                    </button>
                    <button
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                      disabled={saving || loadingSelection}
                      type="button"
                      onClick={handleSave}
                    >
                      <i
                        className={`fas ${saving ? "fa-spinner fa-spin" : "fa-save"}`}
                      />
                      Save
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-5 space-y-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-3">
                      Database Identity
                    </p>
                    <div className="flex items-center gap-3">
                      <ImagePreview
                        alt={selectedLeague.name}
                        src={originalLogo}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {selectedLeague.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {selectedLeague.logo || "No logo"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-3">
                      Effective Identity
                    </p>
                    <div className="flex items-center gap-3">
                      <ImagePreview
                        alt={
                          selectedLeague.effectiveName || selectedLeague.name
                        }
                        src={formLogo}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {form.displayName.trim() ||
                            selectedLeague.effectiveName ||
                            selectedLeague.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {formLogo || "No logo"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Name
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      placeholder={selectedLeague.name || "League name"}
                      type="text"
                      value={form.displayName}
                      onChange={(event) =>
                        updateForm("displayName", event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Logo URL
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      placeholder={selectedLeague.logo || "https://..."}
                      type="url"
                      value={form.imageUrl}
                      onChange={(event) =>
                        updateForm("imageUrl", event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Alternate Name
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      type="text"
                      value={form.altName}
                      onChange={(event) =>
                        updateForm("altName", event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Sport Label
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      type="text"
                      value={form.sport}
                      onChange={(event) =>
                        updateForm("sport", event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      type="text"
                      value={form.country}
                      onChange={(event) =>
                        updateForm("country", event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Formed Year
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      type="text"
                      value={form.formedYear}
                      onChange={(event) =>
                        updateForm("formedYear", event.target.value)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    rows={3}
                    value={form.reason}
                    onChange={(event) =>
                      updateForm("reason", event.target.value)
                    }
                  />
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 mb-3">
                    Provider References
                  </p>
                  {selectedLeague.providerRefs?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedLeague.providerRefs.map((ref) => (
                        <span
                          className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs text-gray-600 dark:text-gray-300"
                          key={providerRefLabel(ref)}
                        >
                          {providerRefLabel(ref)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No provider references
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 dark:border-red-900/60 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                    disabled={
                      !hasEnabledOverride(selectedLeague) ||
                      saving ||
                      loadingSelection
                    }
                    type="button"
                    onClick={() => setDisableConfirmOpen(true)}
                  >
                    <i className="fas fa-ban" />
                    Disable Override
                  </button>

                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                    disabled={saving || loadingSelection}
                    type="button"
                    onClick={handleSave}
                  >
                    <i
                      className={`fas ${saving ? "fa-spinner fa-spin" : "fa-save"}`}
                    />
                    Save Override
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {disableConfirmOpen && selectedLeague && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 px-4">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300">
                <i className="fas fa-ban" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Disable override?
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {selectedLeague.effectiveName || selectedLeague.name} will
                  immediately fall back to its database identity.
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                disabled={saving}
                type="button"
                onClick={() => setDisableConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                disabled={saving}
                type="button"
                onClick={handleDisable}
              >
                <i
                  className={`fas ${saving ? "fa-spinner fa-spin" : "fa-ban"}`}
                />
                Disable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSports;
